import type { ReactNode } from "react";
import type { Breakpoint, GridSize } from "@mui/material";
import {
  Badge,
  Checkbox,
  Grid,
  IconButton,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ActionType,
  ListDensity,
  SortingOrder,
  useContactMetierActions,
  useSearch,
} from "providers";
import { grey } from "@mui/material/colors";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { getPlural } from "utils";

type ResponsiveStyleValue<T> = T | Array<T | null> | { [key in Breakpoint]?: T | null };
const denseColumnWidths: Array<ResponsiveStyleValue<GridSize>> = [
  1.5,
  { xs: 2, sm: 1.5 },
  1.5,
  3,
  "grow",
];
const confortableColumnWidths: Array<ResponsiveStyleValue<GridSize>> = [1.5, 3, 1.5, 2.5, "grow"];

export function ContactMetiersTableHead(): ReactNode {
  const { filteredContactsMetiers, order, setSortColumn, orderBy, sortableTableHeads } =
    useSearch();
  const {
    deselectAll,
    openActionDialog,
    selectedContactMetiers,
    selectOnlyThese,
    density,
    setDensity,
  } = useContactMetierActions();

  const isAllSelected = filteredContactsMetiers.length === selectedContactMetiers.length;

  return (
    <Grid
      container
      sx={{
        borderBottom: `1px solid ${grey[300]}`,
        pl: 0.5,
        pt: 0.5,
        alignItems: "center",
        justifyContent: "flex-end",
      }}
      spacing={1}>
      <Grid size="auto">
        <Tooltip title={!isAllSelected ? "Tout sélectionner" : "Tout désélectionner"}>
          <Checkbox
            color="primary"
            indeterminate={
              selectedContactMetiers.length > 0 &&
              selectedContactMetiers.length < filteredContactsMetiers.length
            }
            checked={
              filteredContactsMetiers.length > 0 &&
              selectedContactMetiers.length === filteredContactsMetiers.length
            }
            onChange={
              isAllSelected
                ? deselectAll
                : () => {
                    selectOnlyThese(filteredContactsMetiers);
                  }
            }
          />
        </Tooltip>
      </Grid>
      {sortableTableHeads.map((column, i) => (
        <Grid
          size={
            (density === ListDensity.DENSE ? denseColumnWidths : confortableColumnWidths)?.[i] ??
            "auto"
          }
          sx={{ paddingY: 0.5, paddingX: 1 }}
          key={column.id}>
          <TableSortLabel
            active={orderBy === column.id}
            direction={orderBy === column.id ? order : SortingOrder.ASC}
            onClick={() => {
              setSortColumn(column);
            }}>
            <Typography sx={{ fontWeight: 600 }}>{column.libelle}</Typography>
          </TableSortLabel>
        </Grid>
      ))}
      <Grid container size="auto" sx={{ pl: 1 }}>
        <Grid>
          <ToggleButtonGroup
            value={density}
            exclusive
            onChange={(_e, value: ListDensity) => {
              setDensity(value);
            }}
            size="small"
            sx={{ mb: "-4px", mt: "4px" }}>
            <ToggleButton value={ListDensity.CONFORTABLE}>
              <Tooltip title="Affichage complet" placement="bottom">
                <DensityMediumIcon sx={{ fontSize: "1.55em" }} />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={ListDensity.DENSE}>
              <Tooltip title="Affichage densifié" placement="bottom">
                <FormatAlignJustifyIcon sx={{ fontSize: "1.55em" }} />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        <Grid>
          <Tooltip title="Modifier toutes les lignes sélectionnées" placement="bottom">
            <span>
              <IconButton
                color="primary"
                size="small"
                sx={{ mb: "-4px" }}
                onClick={() => {
                  openActionDialog(ActionType.EDIT_MULTIPLE);
                }}
                disabled={selectedContactMetiers.length === 0}>
                <EditNoteIcon sx={{ fontSize: "2em" }} />
                <Badge
                  sx={{ top: -12, right: 2 }}
                  badgeContent={selectedContactMetiers.length}
                  color="primary"
                  overlap="circular"
                />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
      <Grid size={12} sx={{ mt: "-20px", pointerEvents: "none" }}>
        <Typography variant="caption">
          {selectedContactMetiers.length > 0
            ? `${selectedContactMetiers.length} sélectionné${getPlural(selectedContactMetiers.length)} sur ${filteredContactsMetiers.length}`
            : "\u00a0"}
        </Typography>
      </Grid>
    </Grid>
  );
}
